import { useNavigation, usePromotion, useCategory, useProduct } from "@utilities/hooks";
import { Promotion } from "@utilities/types";
import { useCallback, useEffect, useMemo, useState } from "react";

export enum PromotionFilterEnum {
    ALL = 'all',
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    ARCHIVED = 'archived'
}

export const useController = () => {

    const { goBack } = useNavigation();
    const {
        allPromotions, onCreatePromotion,
        onUpdatePromotion, onDeletePromotion,
        onGetAllOrganisationPromotion,
        campaigns, onGetAllCampaigns,
    } = usePromotion();

    const { categories, onGetAllOrganisationCategories } = useCategory();
    const { products, onGetAllOrganisationProducts } = useProduct();

    // Deux modals distinctes: une pour les campagnes, une pour les codes promo
    const [createCampaignModal, setCreateCampaignModal] = useState<boolean>(false);
    const [createPromoCodeModal, setCreatePromoCodeModal] = useState<boolean>(false);
    const [searchPromotion, setSearchPromotion] = useState<string>("");
    const [filterType, setFilterType] = useState<PromotionFilterEnum>(PromotionFilterEnum.ACTIVE);

    const filteredPromotions = useMemo(() => {
        let filtered = allPromotions;

        // Filtre par statut
        if (filterType === PromotionFilterEnum.ACTIVE) {
            const currentDate = new Date();
            filtered = allPromotions.filter((promotion) =>
                !promotion.isArchived &&
                promotion.isActive &&
                new Date(promotion.startDate) <= currentDate &&
                new Date(promotion.endDate) >= currentDate
            );
        } else if (filterType === PromotionFilterEnum.INACTIVE) {
            const currentDate = new Date();
            filtered = allPromotions.filter((promotion) =>
                !promotion.isArchived && (
                    !promotion.isActive ||
                    new Date(promotion.startDate) > currentDate ||
                    new Date(promotion.endDate) < currentDate
                )
            );
        } else if (filterType === PromotionFilterEnum.ARCHIVED) {
            filtered = allPromotions.filter((promotion) => promotion.isArchived);
        } else if (filterType === PromotionFilterEnum.ALL) {
            // Pour "Toutes", on affiche toutes les promotions (y compris les archivées)
            filtered = allPromotions;
        }

        // Filtre par recherche
        if (!searchPromotion) return filtered;

        return filtered.filter((promotion) => {
            return promotion?.code!.toLowerCase().includes(searchPromotion.toLowerCase());
        });
    }, [allPromotions, searchPromotion, filterType]);

    useEffect(() => {
        onGetAllOrganisationPromotion();
        onGetAllOrganisationCategories();
        onGetAllOrganisationProducts();
        onGetAllCampaigns();
    }, []);

    const onToggleCampaignModal = useCallback(() => {
        setCreateCampaignModal((prev) => !prev);
    }, []);

    const onTogglePromoCodeModal = useCallback(() => {
        setCreatePromoCodeModal((prev) => !prev);
    }, []);

    const handleCreatePromotion = useCallback(async (promotionData: Partial<Promotion>) => {
        await onCreatePromotion(promotionData);
        setCreateCampaignModal(false);
        setCreatePromoCodeModal(false);
    }, [onCreatePromotion]);

    const handleEditPromotion = useCallback(async (promotionData: Partial<Promotion>) => {
        await onUpdatePromotion(promotionData?._id!, promotionData);
    }, [onUpdatePromotion]);

    const handleDeletePromotion = useCallback(async (id: string) => {
        await onDeletePromotion(id);
    }, [onDeletePromotion]);

    const handleToggleActivePromotion = useCallback(async (promotion: Promotion) => {
        await onUpdatePromotion(promotion._id!, {
            ...promotion,
            isActive: !promotion.isActive
        });
    }, [onUpdatePromotion]);

    const handleFilterChange = useCallback((filter: PromotionFilterEnum) => {
        setFilterType(filter);
    }, []);

    const getFilterLabel = useCallback((filter: PromotionFilterEnum) => {
        switch (filter) {
            case PromotionFilterEnum.ALL:
                return 'Toutes';
            case PromotionFilterEnum.ACTIVE:
                return 'Actives';
            case PromotionFilterEnum.INACTIVE:
                return 'Inactives';
            case PromotionFilterEnum.ARCHIVED:
                return 'Archivées';
            default:
                return 'Toutes';
        }
    }, []);

    return {
        // Modals pour campagnes et codes promo
        createCampaignModal, createPromoCodeModal,
        onToggleCampaignModal, onTogglePromoCodeModal,
        // Données et actions
        filteredPromotions, allPromotions,
        handleCreatePromotion, goBack, filterType,
        setSearchPromotion, handleEditPromotion,
        handleDeletePromotion,
        handleFilterChange, getFilterLabel, handleToggleActivePromotion,
        // Données pour les campagnes
        allCategories: categories, allProducts: products, campaigns,
    }
}
